import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [msg, setMsg] = useState('');

    const fetchProducts = async () => {
        setLoading(true);
        try {
            // Try /user/all-items first
            const res = await axiosInstance.get('/user/all-items');
            const items = res.data.data || res.data.items || res.data || [];
            if (Array.isArray(items) && items.length > 0) {
                setProducts(items);
            } else {
                // Fallback: extract products from registerlistvendors
                const vendorRes = await axiosInstance.get('/auth/registerlistvendors');
                const vendors = vendorRes.data.vendors || [];
                const extracted = vendors.flatMap(v =>
                    (v.products || []).map(p => ({
                        ...p,
                        vendor: { businessName: v.businessName, email: v.email }
                    }))
                );
                setProducts(extracted);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            // Fallback to vendor products list
            try {
                const vendorRes = await axiosInstance.get('/auth/registerlistvendors');
                const vendors = vendorRes.data.vendors || [];
                const extracted = vendors.flatMap(v =>
                    (v.products || []).map(p => ({
                        ...p,
                        vendor: { businessName: v.businessName, email: v.email }
                    }))
                );
                setProducts(extracted);
            } catch {
                setMsg('Failed to fetch products.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const filtered = products.filter(p =>
        (p.name || p.productName || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.category?.name || p.category || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.vendor?.businessName || p.vendor?.vendorName || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="products-page">
            {/* Page Header */}
            <div className="ph">
                <div>
                    <h1>Products &amp; Inventory</h1>
                    <p>{products.length} listed items across vendors</p>
                </div>
            </div>

            {/* Summary strip */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Products', val: products.length },
                    { label: 'In Stock', val: products.filter(p => p.trackQuantity > 0 || p.status !== 'Out of Stock').length },
                    { label: 'Out of Stock', val: products.filter(p => p.trackQuantity === 0 || p.status === 'Out of Stock').length },
                ].map(s => (
                    <div key={s.label} className="card" style={{ padding: '12px 18px', flex: '1', minWidth: 130 }}>
                        <div style={{ fontSize: 10, color: 'var(--tx2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                            {s.label}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)', marginTop: 5 }}>
                            {s.val}
                        </div>
                    </div>
                ))}
            </div>

            {/* Search Toolbar */}
            <div className="toolbar">
                <div className="search-box">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        placeholder="Search by product name, category, or vendor…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && <button className="clear-btn" onClick={() => setSearch('')}>✕</button>}
                </div>
                <span style={{ fontSize: 12, color: 'var(--tx2)', flexShrink: 0 }}>
                    {filtered.length} of {products.length} products
                </span>
            </div>

            {msg && (
                <div className="alert alert-error" style={{ marginBottom: 12 }}>
                    {msg}
                    <button className="alert-close" onClick={() => setMsg('')}>✕</button>
                </div>
            )}

            {/* Table */}
            <div className="tbl-wrap">
                <div className="tbl-scroll">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner" />
                            <p>Fetching products…</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-boxes" style={{ fontSize: 28, color: 'var(--bdr)' }} />
                            <p>{search ? 'No products match your search.' : 'No products found.'}</p>
                        </div>
                    ) : (
                        <table className="tbl" style={{ minWidth: 800 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 36 }}>#</th>
                                    <th>Product</th>
                                    <th>Vendor</th>
                                    <th>Category</th>
                                    <th>Price</th>
                                    <th>Discount Price</th>
                                    <th>Stock / Qty</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((prod, i) => {
                                    const pName = prod.name || prod.productName || 'Unnamed Product';
                                    const pVendor = prod.vendor?.businessName || prod.vendor?.vendorName || prod.vendor?.name || '—';
                                    const pCat = typeof prod.category === 'object' ? prod.category?.name : (prod.category || '—');
                                    const imgUrl = prod.images?.[0]?.url || prod.images?.[0] || prod.image?.url;
                                    const isOut = prod.trackQuantity === 0 || prod.status === 'Out of Stock';

                                    return (
                                        <tr key={prod._id || i}>
                                            <td className="col-num">{i + 1}</td>
                                            <td>
                                                <div className="cust-cell">
                                                    {imgUrl ? (
                                                        <img src={imgUrl} alt="" className="cust-av" style={{ borderRadius: 6, objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="cust-av" style={{ borderRadius: 6 }}>
                                                            <i className="fas fa-box" style={{ fontSize: 12 }} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="cust-name">{pName}</div>
                                                        {prod.unit && <div className="cust-sub">{prod.unit}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td><strong>{pVendor}</strong></td>
                                            <td><span className="badge badge-orange">{pCat}</span></td>
                                            <td>₹{(prod.price || 0).toLocaleString('en-IN')}</td>
                                            <td>{prod.discountPricing ? `₹${prod.discountPricing.toLocaleString('en-IN')}` : '—'}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={`num-cell${isOut ? ' zero' : ''}`}>
                                                    {prod.trackQuantity ?? prod.quantity ?? '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`store-badge ${!isOut ? 'store-open' : 'store-closed'}`}>
                                                    <span className="store-dot" />
                                                    {!isOut ? 'In Stock' : 'Out of Stock'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductList;
